import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";

const FEATURES = [
  ["Live campaign page", true, true, true],
  ["Reward tiers (unlimited)", true, true, true],
  ["Community discovery", true, true, true],
  ["Verified creator badge", false, true, true],
  ["Featured placement", false, true, true],
  ["Advanced analytics", false, false, true],
  ["Dedicated launch coach", false, false, true],
  ["Priority payout (48h)", false, false, true]
];

const PLANS = [
  { name: "Solo", price: "5%", desc: "Perfect for independent creators.", features: ["5% platform fee", "Standard payment processing", "Community discovery", "Ships to 120+ countries"], cta: "Start free", highlight: false },
  { name: "Studio", price: "5%", desc: "For teams & production runs.", features: ["Everything in Solo", "Verified creator badge", "Featured placement", "Team collaboration seats"], cta: "Start free", highlight: true },
  { name: "Enterprise", price: "Custom", desc: "For established brands & institutions.", features: ["Everything in Studio", "Priority payouts", "Dedicated launch coach", "Custom SLA & contracts"], cta: "Contact sales", highlight: false }
];

const FAQ = [
  { q: "When am I charged the platform fee?", a: "Only when your campaign successfully hits its funding goal. If it doesn't, you owe nothing." },
  { q: "Are payment processing fees separate?", a: "Yes. Standard payment processing (typically 2.9% + $0.30) is billed by our payment partner and applies to successful campaigns." },
  { q: "What countries can I raise from?", a: "Creators from 40+ supported countries can launch campaigns and receive payouts to a local bank account." },
  { q: "Is there a minimum funding goal?", a: "There's no strict minimum, but we recommend at least $2,500 to cover production and shipping realistically." }
];

const Pricing = () => {
  return (
    <div data-testid="pricing-page">
      <section className="border-b border-warm-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="text-sm font-medium text-saffron-600">Pricing</span>
          <h1 className="mt-2 font-display text-5xl md:text-6xl font-semibold tracking-tight text-navy-800">Simple. Honest. Fair.</h1>
          <p className="mt-4 text-lg text-navy-600 max-w-2xl mx-auto">Only pay if your campaign is successful. No listing fees. No hidden costs.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div key={p.name} data-testid={`plan-${p.name.toLowerCase()}`}
              className={`rounded-3xl p-8 relative ${p.highlight ? "bg-navy-800 text-white border border-slate-900" : "bg-white border border-warm-200"}`}>
              {p.highlight && (<span className="absolute -top-3 left-8 rounded-full bg-saffron-500 text-white text-xs font-semibold px-3 py-1">Most popular</span>)}
              <div className={`font-display text-2xl font-semibold ${p.highlight ? "text-white" : "text-navy-800"}`}>{p.name}</div>
              <div className={`mt-1 text-sm ${p.highlight ? "text-slate-300" : "text-navy-500"}`}>{p.desc}</div>
              <div className={`mt-6 font-display text-5xl font-semibold tracking-tight ${p.highlight ? "text-white" : "text-navy-800"}`}>{p.price}</div>
              <div className={`text-sm mt-1 ${p.highlight ? "text-slate-300" : "text-navy-500"}`}>of funds raised</div>
              <ul className="mt-8 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-saffron-400" : "text-saffron-600"}`} />
                    <span className={p.highlight ? "text-slate-200" : "text-navy-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className={`mt-8 w-full rounded-2xl h-11 ${p.highlight ? "bg-white text-navy-800 hover:bg-slate-100" : "bg-saffron-600 hover:bg-saffron-700 text-white"}`}>
                <Link to="/start">{p.cta}</Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-navy-800 text-center">Compare features</h2>
        <div className="mt-10 overflow-hidden rounded-2xl border border-warm-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-cream-100">
              <tr>
                <th className="text-left p-4 font-medium text-navy-500">Feature</th>
                <th className="p-4 font-medium text-navy-800">Solo</th>
                <th className="p-4 font-medium text-navy-800">Studio</th>
                <th className="p-4 font-medium text-navy-800">Enterprise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {FEATURES.map(([label, a, b, c]) => (
                <tr key={label}>
                  <td className="p-4 text-navy-700">{label}</td>
                  <td className="p-4 text-center">{a ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                  <td className="p-4 text-center">{b ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                  <td className="p-4 text-center">{c ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-navy-800 text-center">Pricing questions</h2>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-warm-200">
              <AccordionTrigger className="text-left font-display font-semibold text-navy-800 hover:text-saffron-600 hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-navy-600 leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
};

export default Pricing;
